import { Alert, Button, DialogContent, Dialog, Snackbar, DialogTitle } from '@mui/material';
import { useContext, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ExportTranscriptionContext, UserContext } from '../../../../context';
import { TranscriptionStoreContext } from '../../../../context/TranscriptionStoreContext/TranscriptionStoreContext';
import { ReTranscribeDialog } from '../../Dialogs';
import DeleteDialog from '../../Dialogs/DeleteDialog';
import RefundDialog from '../../Dialogs/RefundDialog';
import TranscriptionTitleDialog from '../../Dialogs/TranscriptionTitleDialog';
import ActionButton from './ActionButton';
import { DialogTypes, useActionsConfig, type ActionButtonConfig } from './useActionsConfig';
import AdminDetails from '../../../molecules/AdminDetails';
import type { ExportType } from '../../../../entities';

const useStyles = makeStyles()((theme) => ({
  actionsContainer: {
    containerType: 'inline-size',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 'clamp(0.1rem, 1vw, .3rem)',
    fontSize: '1rem',
    color: '#fff',
    marginTop: theme.spacing(1),
    '@container (max-width: 480px)': {
      flexWrap: 'wrap',
    },
  },
}));

interface SongCardActionsProps {
  handleUnlockTranscription: (option: ExportType) => void;
}

const SongCardActions: React.FC<SongCardActionsProps> = ({ handleUnlockTranscription }) => {
  const { classes } = useStyles();
  const { user } = useContext(UserContext);
  const { selectedTranscription, updateTranscriptions } = useContext(TranscriptionStoreContext);
  const { t } = useTranslation();
  const { exportErrorMessage, clearError } = useContext(ExportTranscriptionContext);
  const [openDialog, setOpenDialog] = useState<DialogTypes | null>(null);
  const [showAdminDetails, setShowAdminDetails] = useState(false);

  const setOpenDialogType = (dialogType: DialogTypes, open: boolean) => {
    if (open) {
      setOpenDialog(dialogType);
    } else {
      setOpenDialog(null);
    }
  };

  const { config, onRename, onDelete } = useActionsConfig({
    handleUnlockTranscription,
    setOpenDialogType,
  });
  return (
    <>
      <div className={classes.actionsContainer}>
        <div className={classes.actions}>
          <Snackbar
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={exportErrorMessage !== null}
            onClose={clearError}
            key="exportErrorMsg">
            <Alert
              severity="error"
              action={
                <Button onClick={clearError} variant="text" size="small">
                  {t('home.songbook.error.download_failed_btn_txt')}
                </Button>
              }>
              {exportErrorMessage}
            </Alert>
          </Snackbar>
          {config.map((button: ActionButtonConfig) =>
            button.condition() ? (
              <React.Fragment key={button.key}>
                {button.render ? (
                  button.render()
                ) : (
                  <ActionButton
                    id={`btn_songbook_${button.key}`}
                    iconType={button.iconType}
                    label={button.label}
                    disabled={button.disabled}
                    onClick={button.onClick}
                  />
                )}
              </React.Fragment>
            ) : null
          )}
        </div>
        <RefundDialog
          key={selectedTranscription?.id}
          open={openDialog === DialogTypes.Refund}
          onClose={() => setOpenDialogType(DialogTypes.Refund, false)}
          updateTranscriptions={updateTranscriptions}
          transcription={selectedTranscription}
        />
        <TranscriptionTitleDialog
          open={openDialog === DialogTypes.Rename}
          transcription={selectedTranscription}
          onSubmit={onRename}
          handleOnClose={() => setOpenDialogType(null, null)}
        />

        <DeleteDialog
          open={openDialog === DialogTypes.Delete}
          setOpen={() => setOpenDialogType(null, null)}
          onDelete={onDelete}
        />
        <ReTranscribeDialog
          open={openDialog === DialogTypes.Retranscribe}
          transcription={selectedTranscription}
          onClose={() => setOpenDialogType(null, null)}
        />
      </div>
      {user.role === 'admin' && (
        <Button onClick={() => setShowAdminDetails(true)}>Admin Details</Button>
      )}
      {showAdminDetails && selectedTranscription && user.role === 'admin' && (
        <Dialog open={showAdminDetails} onClose={() => setShowAdminDetails(false)}>
          <DialogTitle>Admin Details</DialogTitle>
          <DialogContent>
            <AdminDetails transcription={selectedTranscription} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default SongCardActions;
